/**
 * JS di base del tema: nessuna dipendenza, custom elements progressivi.
 * Ogni componente si registra qui; le sezioni includono solo markup.
 */
(() => {
  'use strict';

  /** Chiude un <details> quando si clicca fuori o si preme Esc. */
  class DetailsDisclosure extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      if (!this.details) return;
      this.summary = this.details.querySelector('summary');
      this.addEventListener('keyup', (e) => {
        if (e.code === 'Escape' && this.details.open) this.close();
      });
      this.addEventListener('focusout', () => {
        setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close();
        });
      });
      document.addEventListener('click', (e) => {
        if (this.details.open && !this.contains(e.target)) this.close();
      });
    }

    close() {
      this.details.removeAttribute('open');
      this.summary.setAttribute('aria-expanded', 'false');
    }
  }
  customElements.define('details-disclosure', DetailsDisclosure);

  /**
   * Apertura/chiusura animata dei <details data-animate>: l'altezza scorre (Web Animations API) e il contenuto
   * si dissolve; durante la chiusura la classe `is-closing` permette al CSS di ruotare subito l'icona.
   */
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  document.addEventListener('click', (e) => {
    const summary = e.target.closest('details[data-animate] > summary');
    if (!summary || reducedMotion.matches || document.hidden) return;
    const details = summary.parentElement;
    const content = summary.nextElementSibling;
    if (!content) return;
    e.preventDefault();
    if (details.dataset.animating) return;
    const easing = 'cubic-bezier(0.3, 1, 0.3, 1)';
    const finish = () => {
      delete details.dataset.animating;
      details.classList.remove('is-closing');
      details.style.overflow = '';
    };
    details.dataset.animating = '1';
    details.style.overflow = 'hidden';
    content.getAnimations().forEach((a) => a.cancel());
    if (details.open) {
      details.classList.add('is-closing');
      const from = details.offsetHeight;
      const to = summary.offsetHeight;
      content.animate({ opacity: [1, 0] }, { duration: 200, fill: 'forwards' });
      const anim = details.animate({ height: [`${from}px`, `${to}px`] }, { duration: 400, easing });
      anim.onfinish = () => {
        details.open = false;
        content.getAnimations().forEach((a) => a.cancel());
        finish();
      };
    } else {
      const from = details.offsetHeight;
      details.open = true;
      const to = details.offsetHeight;
      content.animate({ opacity: [0, 1], transform: ['translateY(-0.5rem)', 'none'] }, { duration: 500, easing, delay: 60, fill: 'backwards' });
      const anim = details.animate({ height: [`${from}px`, `${to}px`] }, { duration: 500, easing });
      anim.onfinish = finish;
    }
  });

  /** Espone `aria-expanded` sui summary quando il details si apre/chiude. */
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      const summary = details.querySelector(':scope > summary');
      if (summary) summary.setAttribute('aria-expanded', String(details.open));
    },
    true
  );

  /**
   * Intrappola il focus dentro un dialog/drawer aperto. Gli elementi si ricalcolano a ogni Tab: il contenuto
   * cambia mentre è aperto (risultati della ricerca, carrello aggiornato, pannelli nascosti del menu).
   */
  window.themeTrapFocus = (container, initial) => {
    const selector = 'summary, a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([type="hidden"]), select, textarea';
    const focusable = () => [...container.querySelectorAll(selector)].filter((el) => el.getClientRects().length > 0);
    const onKey = (event) => {
      if (event.code !== 'Tab') return;
      const items = focusable();
      const first = items[0];
      const last = items[items.length - 1];
      if (!first) return;
      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        first.focus();
      }
      if ((event.target === container || event.target === first) && event.shiftKey) {
        event.preventDefault();
        last.focus();
      }
    };
    container.addEventListener('keydown', onKey);
    (initial || focusable()[0] || container).focus();
    return () => container.removeEventListener('keydown', onKey);
  };

  window.themeDebounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  /** Formatta un importo in centesimi secondo shop.money_format, con gli stessi separatori che usa Liquid. */
  window.themeMoney = (cents) => {
    const format = window.theme?.moneyFormat || '{{amount}}';
    const value = (Number(cents) || 0) / 100;
    // decimali, separatore delle migliaia, separatore dei decimali
    const fmt = (decimals, thousands, decimal) => {
      const [intero, frazione] = value.toFixed(decimals).split('.');
      const gruppi = intero.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frazione ? gruppi + decimal + frazione : gruppi;
    };
    const map = {
      amount: fmt(2, ',', '.'),
      amount_no_decimals: fmt(0, ',', '.'),
      amount_with_comma_separator: fmt(2, '.', ','),
      amount_no_decimals_with_comma_separator: fmt(0, '.', ','),
      amount_with_apostrophe_separator: fmt(2, "'", '.'),
      amount_with_space_separator: fmt(2, ' ', ','),
      amount_no_decimals_with_space_separator: fmt(0, ' ', ','),
      amount_with_period_and_space_separator: fmt(2, ' ', '.'),
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => map[key] ?? map.amount);
  };

  /** Rivelazione allo scroll degli elementi [data-reveal]. */
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -5% 0px' }
  );
  const observeReveal = (root = document) => {
    root.querySelectorAll('[data-reveal]:not(.is-visible)').forEach((el) => {
      if (reduceMotion) el.classList.add('is-visible');
      else revealObserver.observe(el);
    });
  };
  window.themeObserveReveal = observeReveal;
  observeReveal();
  document.addEventListener('shopify:section:load', (e) => observeReveal(e.target));

  /**
   * «Carica altri» della collezione: chiede la pagina successiva con la Section Rendering API, aggiunge le schede
   * in coda e sostituisce sé stesso con la versione aggiornata (conteggio e link alla pagina dopo). In modalità
   * infinite parte da solo quando si avvicina allo schermo. Se qualcosa va storto si segue il link normale.
   */
  class LoadMore extends HTMLElement {
    connectedCallback() {
      this.link = this.querySelector('[data-load-more-link]');
      if (!this.link) return;
      this.link.addEventListener('click', (e) => {
        e.preventDefault();
        this.load(true);
      });
      if (this.dataset.mode === 'infinite' && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => entries.some((en) => en.isIntersecting) && this.load(false), { rootMargin: '600px 0px' });
        this.observer.observe(this);
      }
    }
    disconnectedCallback() {
      this.observer?.disconnect();
    }
    async load(spostaFocus) {
      if (this.loading) return;
      this.loading = true;
      this.link.classList.add('is-loading');
      this.link.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(this.link.href, window.location.origin);
        url.searchParams.set('section_id', this.dataset.section);
        const res = await fetch(url.toString());
        if (!res.ok) throw new Error(String(res.status));
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const grid = this.parentElement.querySelector('[data-load-more-grid]');
        const nuove = [...doc.querySelectorAll('[data-load-more-grid] > li:not(.collection__promo)')];
        if (!grid || !nuove.length) throw new Error('vuota');
        nuove.forEach((li) => grid.append(li));
        observeReveal(grid);
        // chi ha premuto il pulsante (anche da tastiera) riparte dalla prima scheda nuova
        if (spostaFocus) nuove[0].querySelector('a[href]')?.focus();
        const aggiornato = doc.querySelector('load-more');
        if (aggiornato) this.replaceWith(aggiornato);
        else this.remove();
      } catch (e) {
        window.location.href = this.link.href;
      } finally {
        this.loading = false;
        this.link?.classList.remove('is-loading');
        this.link?.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('load-more', LoadMore);

  /**
   * Carosello orizzontale a scatti: frecce [data-carousel-prev/next], traccia [data-carousel-track].
   * Lo scorrimento avviene di un elemento alla volta; le frecce si disattivano ai bordi.
   */
  class CarouselElement extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      if (!this.track) return;
      this.prev?.addEventListener('click', () => this.scrollByItems(-1));
      this.next?.addEventListener('click', () => this.scrollByItems(1));
      this.update = this.update.bind(this);
      // a scorrimento finito lo stato delle frecce torna a seguire la posizione reale
      this.track.addEventListener(
        'scroll',
        window.themeDebounce(() => {
          this.destinazione = null;
          this.update();
        }, 80),
        { passive: true }
      );
      window.addEventListener('resize', window.themeDebounce(this.update, 120));
      // le frecce si disattivano in base alla larghezza del contenuto: finché le immagini non sono
      // caricate quella misura è sbagliata e la freccia nascerebbe spenta, ignorando il primo clic.
      // L'osservatore rimisura a ogni cambio di dimensione, comprese le tab che diventano visibili.
      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(() => this.update());
        this.observer.observe(this.track);
        if (this.track.firstElementChild) this.observer.observe(this.track.firstElementChild);
      }
      this.track.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', this.update, { once: true });
      });
      window.addEventListener('load', this.update, { once: true });
      // il pulsante è tondo ma il suo riquadro è quadrato: negli angoli, fuori dal cerchio, il clic
      // attraversa e finisce sulla foto del prodotto sotto, che è un link, e apre la scheda invece
      // di scorrere. Qui il clic viene ripreso prima del link e trasformato nello scorrimento.
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-carousel-prev], [data-carousel-next]')) return;
        for (const [bottone, verso] of [[this.prev, -1], [this.next, 1]]) {
          if (!bottone) continue;
          const r = bottone.getBoundingClientRect();
          if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) continue;
          e.preventDefault();
          e.stopPropagation();
          this.scrollByItems(verso);
          return;
        }
      }, true);
      this.update();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    itemWidth() {
      const item = this.track.firstElementChild;
      if (!item) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    scrollByItems(n) {
      const passo = this.itemWidth();
      const max = this.track.scrollWidth - this.track.clientWidth;
      // clic ravvicinati si sommano alla destinazione già in corso, non alla posizione a metà animazione
      const base = this.destinazione ?? this.track.scrollLeft;
      const arrivo = Math.min(max, Math.max(0, Math.round(base / passo) * passo + passo * n));
      if (Math.abs(arrivo - base) < 2) return;
      this.destinazione = arrivo;
      this.update();
      const partenza = this.track.scrollLeft;
      this.track.scrollTo({ left: arrivo, behavior: 'smooth' });
      // il browser porta avanti una sola animazione di scorrimento per volta: se la pagina si sta
      // ancora assestando (caricamento, altro scorrimento in corso) questa viene scartata in silenzio
      // e il clic sembra non fare nulla. Se dopo un istante non si è mosso niente, si salta secco.
      clearTimeout(this.riprova);
      this.riprova = setTimeout(() => {
        if (Math.abs(this.track.scrollLeft - partenza) > 2) return;
        const prima = this.track.style.scrollBehavior;
        this.track.style.scrollBehavior = 'auto';
        this.track.scrollLeft = arrivo;
        this.track.style.scrollBehavior = prima;
        this.destinazione = null;
        this.update();
      }, 140);
    }

    scrollToIndex(i) {
      this.track.scrollTo({ left: this.itemWidth() * i, behavior: 'smooth' });
    }

    currentIndex() {
      return Math.round(this.track.scrollLeft / this.itemWidth());
    }

    update() {
      // traccia non ancora misurabile (tab nascosta, layout non pronto): non spegnere le frecce
      if (!this.track.clientWidth) return;
      // durante lo scorrimento animato lo stato segue la destinazione, non la posizione intermedia.
      // aria-disabled e non disabled: un pulsante disabled scarta il clic, e uno stato vecchio di
      // qualche istante (scorrimento ancora in corso) faceva perdere i clic sulle frecce.
      const pos = this.destinazione ?? this.track.scrollLeft;
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      this.prev?.setAttribute('aria-disabled', String(pos <= 2));
      this.next?.setAttribute('aria-disabled', String(pos >= max));
      this.classList.toggle('is-scrollable', this.track.scrollWidth > this.track.clientWidth + 2);
      this.dispatchEvent(new CustomEvent('carousel:change', { detail: { index: this.currentIndex() } }));
    }
  }
  customElements.define('carousel-element', CarouselElement);

  /** Tab generiche: [role=tab] con aria-controls, pannelli nascosti con `hidden`. */
  class TabsElement extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      if (!this.tabs.length) return;
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.activate(tab));
        tab.addEventListener('keydown', (e) => {
          const next = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? i + 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? i - 1 : null;
          if (next === null) return;
          e.preventDefault();
          const target = this.tabs[(next + this.tabs.length) % this.tabs.length];
          this.activate(target);
          target.focus();
        });
      });
    }

    activate(tab) {
      this.tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) {
          panel.hidden = !on;
          if (on) {
            observeReveal(panel);
            panel.querySelectorAll('carousel-element').forEach((c) => c.update?.());
          }
        }
      });
      this.dispatchEvent(new CustomEvent('tabs:change', { detail: { tab } }));
    }
  }
  customElements.define('tabs-element', TabsElement);

  /** Swatch nelle card: al passaggio/click mostrano l'immagine della variante. */
  const swapSwatch = (swatch) => {
    const card = swatch.closest('[data-product-card]');
    const img = card?.querySelector('.product-card__img--primary');
    if (!img || !swatch.dataset.swatchMedia) return;
    img.src = swatch.dataset.swatchMedia;
    if (swatch.dataset.swatchSrcset) img.srcset = swatch.dataset.swatchSrcset;
    card.querySelectorAll('.product-card__swatch').forEach((s) => s.classList.toggle('is-active', s === swatch));
    // immagine e titolo portano alla variante mostrata, non a quella predefinita
    if (swatch.href) card.querySelectorAll('.product-card__image-link, .product-card__title, .product-card__more').forEach((a) => (a.href = swatch.href));
    if (swatch.title) card.dataset.trackVariant = swatch.title;
    const secondary = card.querySelector('.product-card__img--secondary');
    if (!secondary) return;
    // seconda foto dello stesso colore; se quel colore non ce l'ha, l'hover non cambia immagine
    if (swatch.dataset.swatchSecondary) {
      secondary.src = swatch.dataset.swatchSecondary;
      if (swatch.dataset.swatchSecondarySrcset) secondary.srcset = swatch.dataset.swatchSecondarySrcset;
      secondary.hidden = false;
    } else {
      secondary.hidden = true;
    }
  };
  document.addEventListener('mouseover', (e) => {
    const swatch = e.target.closest('.product-card__swatch[data-swatch-media]');
    if (swatch) swapSwatch(swatch);
  });
  document.addEventListener('focusin', (e) => {
    const swatch = e.target.closest('.product-card__swatch[data-swatch-media]');
    if (swatch) swapSwatch(swatch);
  });

  /**
   * [data-shuffle-swatches]: ogni card parte da un colore a caso, diverso da quello della card precedente
   * quando possibile. Gira prima che le immagini lazy partano, quindi si scarica solo la foto scelta.
   */
  const shuffleSwatches = () =>
    document.querySelectorAll('[data-shuffle-swatches]').forEach((list) => {
      let precedente = null;
      list.querySelectorAll('[data-product-card]').forEach((card) => {
        const swatches = [...card.querySelectorAll('.product-card__swatch[data-swatch-media]')];
        if (swatches.length < 2) {
          precedente = swatches[0]?.title ?? null;
          return;
        }
        const diversi = swatches.filter((s) => s.title !== precedente);
        const scelto = diversi[Math.floor(Math.random() * diversi.length)];
        precedente = scelto.title;
        if (!scelto.classList.contains('is-active')) swapSwatch(scelto);
      });
    });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', shuffleSwatches);
  else shuffleSwatches();
})();
